import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Route, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Client, CreateLoadLimitDataIn, GuidDictionaryItem, LimitType, MeterPhase, MeterTargetType } from '../../../api/api-reference';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-load-limit-setup',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, FormsModule, CommonModule, TranslateModule, AngularSvgIconModule],
  templateUrl: './load-limit-setup.component.html',
  styleUrl: './load-limit-setup.component.scss'
})
export class LoadLimitSetupComponent implements OnInit {
  data: CreateLoadLimitDataIn = new CreateLoadLimitDataIn();
  loadLimitForm: FormGroup = this.fb.group({
    id: [null],
    target: [null, Validators.required],
    meterNumber: [null, [Validators.pattern('^[0-9]+$')]],
    limitType: [null, Validators.required],
    value: [null, [Validators.required, Validators.min(1), Validators.pattern(/^[1-9]\d{0,18}$/)]], //64bit
    waitTime: [null, [Validators.min(1), Validators.max(4294967295), Validators.pattern(/^[1-9]\d*$/)]],
    regions: [null],
    assetType: [null],
    serviceType: [null],
    meterPhase: [null]
  });
  public MeterTargetType = MeterTargetType;
  public LimitType = LimitType;

  submitting: boolean = false;
  submitted: boolean = false;
  assetTypes: GuidDictionaryItem[] = [];
  serviceTypes: GuidDictionaryItem[] = [];
  regions: GuidDictionaryItem[] = [];
  selectedRegions: GuidDictionaryItem[] = [];
  loadLimitId: string | undefined;

  meterTargetTypeDictionary: MeterTargetType[] = Object.keys(MeterTargetType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => MeterTargetType[key as keyof typeof MeterTargetType]);
  limitTypeDictionary = Object.values(LimitType).map(type => ({
    value: type,
    label: type === LimitType.Current ? "Current" : "Demand"
  }));
  meterPhaseDictionary = Object.values(MeterPhase).map(type => ({
    value: type,
    label: type === MeterPhase.OnePhase ? "Single Phase" : "Three Phase"
  }));

  constructor(private fb: FormBuilder,
    private toastr: ToastrService,
    public authService: AuthService,
    private client: Client,
    private router: Router
  ) {
  }
  ngOnInit(): void {
    const state = history.state;
    if (state && state.loadLimitData) {
      this.loadLimitId = state.loadLimitData;
    }

    if (this.loadLimitId)
      this.getLoadLimit();
    this.loadLimitForm.get('target')?.setValue(MeterTargetType.SingleMeter);
    this.getAssetTypes();
    this.getRegions();
    this.getServiceTypes();
  }

  getLoadLimit() {
    this.client.getLoadLimit(this.loadLimitId!).subscribe({
      next: (response) => {
        this.selectedRegions = (response.regions ?? []).map((id, index) => {
          const item = new GuidDictionaryItem();
          item.value = id;
          item.name = response.regionNames?.[index] ?? '';
          return item;
        });

        this.loadLimitForm.patchValue({
          id: response.id,
          target: response.target,
          meterNumber: response.meterNumber || '',
          limitType: response.limitType,
          value: response.value,
          waitTime: response.waitTime ?? null,
          regions: response.regions ?? null,
          assetType: response.assetType ?? null,
          serviceType: response.serviceType ?? null,
          meterPhase: response.meterPhase ?? null
        });
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  onRegionSelected(event: Event) {
    const select = event.target as HTMLSelectElement;
    const selectedValue = select.value;

    if (!selectedValue) {
      return;
    }

    const region = this.regions.find(r => r.value === selectedValue);
    if (!region) {
      return;
    }

    const alreadySelected = this.selectedRegions.some(r => r.value === region.value);
    if (alreadySelected) {
      select.value = '';
      return;
    }

    this.selectedRegions.push(region);

    this.loadLimitForm.patchValue({
      regions: this.selectedRegions.map(r => r.value)
    });

    select.value = '';
  }

  removeRegion(region: GuidDictionaryItem) {
    this.selectedRegions = this.selectedRegions.filter(r => r.value !== region.value);

    this.loadLimitForm.patchValue({
      regions: this.selectedRegions.length ? this.selectedRegions.map(r => r.value) : null
    });
  }

  getAssetTypes() {
    this.client.getAllAssetTypesForOptions(true).subscribe({
      next: (response) => {
        this.assetTypes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err.message);
      },
    });
  }
  getRegions() {
    this.client.getAllRegionsForOptions().subscribe({
      next: (response) => {
        this.regions = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  getServiceTypes() {
    this.client.getAllServiceTypesForOptions().subscribe({
      next: (response) => {
        this.serviceTypes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  clearRegions() {
    this.selectedRegions = [];
    this.loadLimitForm.patchValue({ regions: null });
  }

  clearServiceType() {
    this.loadLimitForm.patchValue({ serviceType: null });
  }

  clearMeterPhase() {
    this.loadLimitForm.patchValue({ meterPhase: null });
  }

  onSubmit() {
    try {
      this.submitting = true;

      Object.keys(this.loadLimitForm.controls).forEach(key => {
        const controlErrors = this.loadLimitForm.get(key)?.errors;
        if (controlErrors != null) {
          console.log('Field:', key, 'Errors:', controlErrors);
        }
      });

      const formValue = this.loadLimitForm.getRawValue();
      const data = new CreateLoadLimitDataIn({
        id: this.loadLimitId,
        target: formValue.target as MeterTargetType,
        limitType: formValue.limitType as LimitType,
        meterNumber: formValue.meterNumber,
        value: Number(formValue.value),
        waitTime: formValue.waitTime ? Number(formValue.waitTime) : undefined,
        regions: formValue.regions,
        assetTypeId: formValue.assetType,
        serviceTypeId: formValue.serviceType,
        meterPhase: formValue.meterPhase
      });
      this.client.addLoadLimit(data).subscribe({
        next: () => {
          this.toastr.success('Load limit successfully saved.');
          this.router.navigate(['/load-limit']);
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    } finally {
      this.submitted = true;
      this.submitting = false;
    }
  }
  selectRadioOption(fieldName: string, value: any) {
    this.loadLimitForm.patchValue({ [fieldName]: value });
    //reset all values
    this.loadLimitForm.patchValue({
      meterNumber: null,
      limitType: null,
      value: null,
      waitTime: null,
      regions: null,
      assetType: null,
      serviceType: null,
      meterPhase: null
    });

    this.selectedRegions = [];
  }
  formErrorsExists(controlName: string): boolean {
    const control = this.loadLimitForm.get(controlName);
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }
}
